import axios from 'axios';
import {
  WhatsAppGroup,
  CreateGroupRequest,
  UpdateGroupRequest,
  UpdateParticipantsRequest,
  UpdateSettingsRequest,
  SendInvitesRequest,
  ListGroupsResponse,
  GroupDetailsResponse,
  CreateGroupResponse,
  InviteCodeResponse,
  RevokeInviteResponse,
  ApiResponse
} from '../types/groupTypes';

// ============================================================================
// API CLIENT CONFIGURATION
// ============================================================================

// Em produção, usa URL relativa (mesmo domínio)
// Em desenvolvimento, usa variável de ambiente ou localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 
  (import.meta.env.MODE === 'production' ? '/api' : 'http://localhost:3001/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token'); // ← CORRIGIDO: era 'token', agora é 'auth_token'
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para tratar erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado, redirecionar para login
      localStorage.removeItem('auth_token'); // ← CORRIGIDO: era 'token', agora é 'auth_token'
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// GROUPS API SERVICE
// ============================================================================

export const groupsApi = {
  /**
   * Listar todos os grupos do tenant
   * @param instanceName Nome da instância (opcional)
   * @param forceRefresh Forçar sincronização com Evolution API
   */
  async fetchGroups(instanceName?: string, forceRefresh: boolean = false): Promise<WhatsAppGroup[]> {
    try {
      const params: any = {};
      if (instanceName) params.instanceName = instanceName;
      if (forceRefresh) params.forceRefresh = 'true';

      const response = await api.get<ListGroupsResponse>('/evolution/groups', { params });
      return response.data.groups;
    } catch (error: any) {
      console.error('Erro ao buscar grupos:', error);
      throw new Error(error.response?.data?.error || 'Erro ao buscar grupos');
    }
  },

  /**
   * Buscar detalhes de um grupo específico
   * @param groupJid ID do grupo
   * @param forceRefresh Forçar sincronização
   */
  async fetchGroupDetails(groupJid: string, forceRefresh: boolean = false): Promise<WhatsAppGroup> {
    try {
      const params: any = {};
      if (forceRefresh) params.forceRefresh = 'true';

      const response = await api.get<GroupDetailsResponse>(`/evolution/groups/${groupJid}`, { params });
      return response.data.group;
    } catch (error: any) {
      console.error('Erro ao buscar detalhes do grupo:', error);
      throw new Error(error.response?.data?.error || 'Erro ao buscar detalhes do grupo');
    }
  },

  /**
   * Criar um novo grupo
   * @param data Dados do grupo
   */
  async createGroup(data: CreateGroupRequest): Promise<WhatsAppGroup> {
    try {
      const response = await api.post<CreateGroupResponse>('/evolution/groups/create', data);
      return response.data.group;
    } catch (error: any) {
      console.error('Erro ao criar grupo:', error);
      throw new Error(error.response?.data?.error || 'Erro ao criar grupo');
    }
  },

  /**
   * Atualizar informações do grupo
   * @param groupJid ID do grupo
   * @param data Dados a atualizar
   */
  async updateGroup(groupJid: string, data: UpdateGroupRequest): Promise<WhatsAppGroup> {
    try {
      const response = await api.put<ApiResponse<{ group: WhatsAppGroup }>>(
        `/evolution/groups/${groupJid}`,
        data
      );
      return response.data.data!.group;
    } catch (error: any) {
      console.error('Erro ao atualizar grupo:', error);
      throw new Error(error.response?.data?.error || 'Erro ao atualizar grupo');
    }
  },

  /**
   * Sair de um grupo
   * @param groupJid ID do grupo
   */
  async leaveGroup(groupJid: string): Promise<void> {
    try {
      await api.delete(`/evolution/groups/${groupJid}/leave`);
    } catch (error: any) {
      console.error('Erro ao sair do grupo:', error);
      throw new Error(error.response?.data?.error || 'Erro ao sair do grupo');
    }
  },

  /**
   * Adicionar participantes ao grupo
   * @param groupJid ID do grupo
   * @param participants Lista de JIDs dos participantes
   */
  async addParticipants(groupJid: string, participants: string[]): Promise<void> {
    try {
      const data: UpdateParticipantsRequest = {
        action: 'add',
        participants
      };
      await api.put(`/evolution/groups/${groupJid}/participants`, data);
    } catch (error: any) {
      console.error('Erro ao adicionar participantes:', error);
      throw new Error(error.response?.data?.error || 'Erro ao adicionar participantes');
    }
  },

  /**
   * Remover participantes do grupo
   * @param groupJid ID do grupo
   * @param participants Lista de JIDs dos participantes
   */
  async removeParticipants(groupJid: string, participants: string[]): Promise<void> {
    try {
      const data: UpdateParticipantsRequest = {
        action: 'remove',
        participants
      };
      await api.put(`/evolution/groups/${groupJid}/participants`, data);
    } catch (error: any) {
      console.error('Erro ao remover participantes:', error);
      throw new Error(error.response?.data?.error || 'Erro ao remover participantes');
    }
  },

  /**
   * Promover participantes a administradores
   * @param groupJid ID do grupo
   * @param participants Lista de JIDs dos participantes
   */
  async promoteParticipants(groupJid: string, participants: string[]): Promise<void> {
    try {
      const data: UpdateParticipantsRequest = {
        action: 'promote',
        participants
      };
      await api.put(`/evolution/groups/${groupJid}/participants`, data);
    } catch (error: any) {
      console.error('Erro ao promover participantes:', error);
      throw new Error(error.response?.data?.error || 'Erro ao promover participantes');
    }
  },

  /**
   * Rebaixar administradores a membros
   * @param groupJid ID do grupo
   * @param participants Lista de JIDs dos participantes
   */
  async demoteParticipants(groupJid: string, participants: string[]): Promise<void> {
    try {
      const data: UpdateParticipantsRequest = {
        action: 'demote',
        participants
      };
      await api.put(`/evolution/groups/${groupJid}/participants`, data);
    } catch (error: any) {
      console.error('Erro ao rebaixar participantes:', error);
      throw new Error(error.response?.data?.error || 'Erro ao rebaixar participantes');
    }
  },

  /**
   * Obter código de convite do grupo
   * @param groupJid ID do grupo
   */
  async getInviteCode(groupJid: string): Promise<{ inviteCode: string; inviteUrl: string }> {
    try {
      const response = await api.get<InviteCodeResponse>(`/evolution/groups/${groupJid}/invite`);
      return {
        inviteCode: response.data.inviteCode,
        inviteUrl: response.data.inviteUrl
      };
    } catch (error: any) {
      console.error('Erro ao obter código de convite:', error);
      throw new Error(error.response?.data?.error || 'Erro ao obter código de convite');
    }
  },

  /**
   * Revogar código de convite do grupo
   * @param groupJid ID do grupo
   */
  async revokeInviteCode(groupJid: string): Promise<{ inviteCode: string; inviteUrl: string }> {
    try {
      const response = await api.put<RevokeInviteResponse>(`/evolution/groups/${groupJid}/invite/revoke`);
      return {
        inviteCode: response.data.newInviteCode,
        inviteUrl: response.data.newInviteUrl
      };
    } catch (error: any) {
      console.error('Erro ao revogar convite:', error);
      throw new Error(error.response?.data?.error || 'Erro ao revogar convite');
    }
  },

  /**
   * Enviar convites do grupo para contatos
   * @param groupJid ID do grupo
   * @param data Dados do envio
   */
  async sendInvites(groupJid: string, data: SendInvitesRequest): Promise<void> {
    try {
      await api.post(`/evolution/groups/${groupJid}/invite/send`, data);
    } catch (error: any) {
      console.error('Erro ao enviar convites:', error);
      throw new Error(error.response?.data?.error || 'Erro ao enviar convites');
    }
  },

  /**
   * Atualizar configurações do grupo
   * @param groupJid ID do grupo
   * @param data Configurações
   */
  async updateSettings(groupJid: string, data: UpdateSettingsRequest): Promise<WhatsAppGroup> {
    try {
      const response = await api.put<ApiResponse<{ group: WhatsAppGroup }>>(
        `/evolution/groups/${groupJid}/settings`,
        data
      );
      return response.data.data!.group;
    } catch (error: any) {
      console.error('Erro ao atualizar configurações:', error);
      throw new Error(error.response?.data?.error || 'Erro ao atualizar configurações');
    }
  },

  /**
   * Sincronizar grupo com Evolution API
   * @param groupJid ID do grupo
   */
  async syncGroup(groupJid: string): Promise<WhatsAppGroup> {
    try {
      const response = await api.post<ApiResponse<{ group: WhatsAppGroup }>>(
        `/evolution/groups/${groupJid}/sync`
      );
      return response.data.data!.group;
    } catch (error: any) {
      console.error('Erro ao sincronizar grupo:', error);
      throw new Error(error.response?.data?.error || 'Erro ao sincronizar grupo');
    }
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Formatar número de telefone para JID do WhatsApp
 * @param phone Número de telefone
 */
export function formatPhoneToJid(phone: string): string {
  // Remove caracteres não numéricos
  const cleaned = phone.replace(/\D/g, '');
  
  // Adiciona código do país se necessário (Brasil = 55)
  const withCountryCode = cleaned.startsWith('55') ? cleaned : `55${cleaned}`;
  
  // Adiciona sufixo do WhatsApp
  return `${withCountryCode}@s.whatsapp.net`;
}

/**
 * Formatar JID para número de telefone legível
 * @param jid JID do WhatsApp
 */
export function formatJidToPhone(jid: string): string {
  // Remove sufixo @s.whatsapp.net
  const cleaned = jid.replace('@s.whatsapp.net', '');
  
  // Formata número brasileiro
  if (cleaned.startsWith('55') && cleaned.length === 13) {
    // Formato: 55 11 99999-9999
    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
  }
  
  return `+${cleaned}`;
}

/**
 * Validar se é um número de telefone válido
 * @param phone Número de telefone
 */
export function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length >= 10 && cleaned.length <= 15;
}

/**
 * Obter texto da duração efêmera
 * @param duration Duração em segundos
 */
export function getEphemeralDurationText(duration: number): string {
  switch (duration) {
    case 0:
      return 'Desativado';
    case 86400:
      return '24 horas';
    case 604800:
      return '7 dias';
    case 7776000:
      return '90 dias';
    default:
      return `${Math.floor(duration / 86400)} dias`;
  }
}

/**
 * Verificar se o cache do grupo está válido (< 5 min)
 * @param lastSyncedAt Data da última sincronização
 */
export function isCacheValid(lastSyncedAt?: string): boolean {
  if (!lastSyncedAt) return false;
  
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutos
  const now = Date.now();
  const cacheAge = now - new Date(lastSyncedAt).getTime();
  
  return cacheAge < CACHE_TTL;
}

export default groupsApi;

